import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import TrialBanner from "../components/TrialBanner";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Sparkles, TrendingUp, Mail, UserCheck, AlertCircle, RefreshCw, Loader2,
} from "lucide-react";

const ROLE_LABEL = {
  law_enforcement: "Law Enforcement",
  fire_service: "Fire Service",
  ems: "EMS",
  dispatch_psap: "Dispatch / PSAP",
  corrections: "Corrections",
  school_safety: "School Safety",
  military: "Military",
  emergency_medicine: "Emergency Medicine",
  command_staff: "Command Staff",
  training_director: "Training Director",
  wellness_director: "Wellness Director",
  hr: "HR",
  other: "Other",
};

export default function AgencyLeads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/agency/leads");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load lead analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <TrialBanner />
        <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Agency Console · Platform Demand
        </div>
        <h1 className="font-heading text-3xl font-bold">Lead Analytics</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Aggregate lead capture from the anonymous public demo. Use this as a platform demand signal —
          who's discovering Haruki, and at what cadence. Demo respondents who later create accounts count toward conversion.
        </p>

        {loading && (
          <div className="mt-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading lead analytics…
          </div>
        )}

        {error && (
          <div data-testid="leads-error-state" className="mt-8 rounded-md border p-6"
               style={{ borderColor: "hsl(var(--haruki-fire))", background: "hsl(var(--haruki-fire) / 0.06)" }}>
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5" style={{ color: "hsl(var(--haruki-fire))" }} />
              <div className="flex-1">
                <div className="font-heading text-base font-semibold">Couldn't load lead analytics</div>
                <p className="mt-1 text-sm text-muted-foreground">{error}</p>
                <Button data-testid="leads-retry-button" onClick={load} className="mt-4" size="sm"
                        style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}>
                  <RefreshCw className="mr-2 h-3 w-3" /> Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat test="leads-stat-total" icon={Sparkles} label="Total leads" value={data.total} subline="All-time captures" tone="leaf" />
              <Stat test="leads-stat-7d" icon={TrendingUp} label="Last 7 days" value={data.last_7d} subline="Recent demand" tone="teal" />
              <Stat test="leads-stat-30d" icon={Mail} label="Last 30 days" value={data.last_30d} subline="Trending velocity" tone="teal" />
              <Stat
                test="leads-stat-conv"
                icon={UserCheck}
                label="Conversion rate"
                value={`${Math.round((data.conversion_rate || 0) * 100)}%`}
                subline={`${data.converted} of ${data.total} → signed up`}
                tone="leaf"
              />
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="font-heading text-base">Daily lead captures (30 days)</CardTitle></CardHeader>
                <CardContent>
                  <DailyBars series={data.daily} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="font-heading text-base">By stated role</CardTitle></CardHeader>
                <CardContent>
                  {(data.by_role || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No role data yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {data.by_role.map(r => (
                        <div key={r.role} className="flex items-center justify-between text-sm">
                          <span>{ROLE_LABEL[r.role] || r.role}</span>
                          <span className="font-semibold tabular-nums">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-3">
                <CardHeader><CardTitle className="font-heading text-base">Sample Report views by source</CardTitle></CardHeader>
                <CardContent>
                  {(data.sample_report_views || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No tagged views yet. Share <code className="rounded bg-secondary px-1">/demo/sample-report?source=…</code> to start tracking.</div>
                  ) : (
                    <>
                      <div className="mb-4 text-sm">
                        <span className="font-semibold tabular-nums">{data.sample_report_views_total || 0}</span>
                        <span className="ml-1 text-muted-foreground">total views across {data.sample_report_views.length} source{data.sample_report_views.length === 1 ? "" : "s"}</span>
                      </div>
                      <div className="space-y-2">
                        {data.sample_report_views.map((v) => {
                          const max = Math.max(1, ...data.sample_report_views.map(x => x.views));
                          const pct = Math.round((v.views / max) * 100);
                          return (
                            <div key={v.source} data-testid={`sample-views-row-${v.source}`}>
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">{v.source}</span>
                                <span className="font-semibold tabular-nums">{v.views}</span>
                              </div>
                              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full" style={{ background: "hsl(var(--secondary))" }}>
                                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "hsl(var(--haruki-teal))" }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-3">
                <CardHeader><CardTitle className="font-heading text-base">Recent leads</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">When</th>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Source</th>
                        <th className="px-4 py-3 text-right">Touches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent.map((l) => (
                        <tr key={l.email} data-testid={`leads-row-${l.email}`} className="border-b border-border last:border-0">
                          <td className="px-4 py-3 text-muted-foreground">{l.created_at?.slice(0, 10)}</td>
                          <td className="px-4 py-3 font-medium">{l.name || "—"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{l.email}</td>
                          <td className="px-4 py-3">{ROLE_LABEL[l.agency_role] || l.agency_role || "—"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{l.source || "—"}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{l.touch_count || 1}</td>
                        </tr>
                      ))}
                      {data.recent.length === 0 && (
                        <tr><td colSpan="6" className="px-4 py-6 text-center text-sm text-muted-foreground">No leads captured yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
              <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Drip-email delivery (welcome + trial-ending-in-7-days) currently <strong>MOCKED</strong>. DB plumbing is ready —
                add your Resend API key in <code className="rounded bg-secondary px-1">/app/backend/.env</code> as
                <code className="ml-1 rounded bg-secondary px-1">RESEND_API_KEY</code> to enable real delivery.
              </span>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

const TONE = {
  leaf: "hsl(var(--haruki-leaf))",
  teal: "hsl(var(--haruki-teal))",
  rust: "hsl(var(--haruki-rust))",
};

const Stat = ({ icon: Icon, label, value, subline, tone = "leaf", test }) => (
  <Card data-testid={test}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2">
        <div className="rounded-md p-1.5" style={{ background: TONE[tone], color: "white" }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 font-heading text-2xl font-extrabold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{subline}</div>
    </CardContent>
  </Card>
);

const DailyBars = ({ series = [] }) => {
  const max = Math.max(1, ...series.map((s) => s.leads));
  return (
    <div className="flex h-40 items-end gap-1">
      {series.map((s) => {
        const pct = Math.round((s.leads / max) * 100);
        return (
          <div key={s.date} className="flex flex-1 flex-col items-center">
            <div
              className="w-full rounded-t transition-opacity hover:opacity-80"
              style={{
                height: `${Math.max(pct, 4)}%`,
                background: s.leads > 0 ? "hsl(var(--haruki-leaf))" : "hsl(var(--border))",
              }}
              title={`${s.date}: ${s.leads}`}
            />
          </div>
        );
      })}
    </div>
  );
};
